/* eslint-disable react-hooks/exhaustive-deps */
import { Navigate } from 'react-router-dom'; // Updated import
import _ from 'lodash';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { getUserDetails } from '../utils/orgName';
import { breadCrumbsActions } from '../redux/slices/breadcrumbs/breadcrumbsSlice';
import ROLES from '../utils/roles';
import ErrorPage from '../components/ErrorPage';
import AppLoader from '../components/AppLoader';
import { AGILE_ORG } from '../utils/constants';
import { groupLoaderActions } from '../redux/slices/groupLoaderSlice';
import { useAdminAccess } from '../hooks/useAdminAccess';
import { useHiringCandidateLanding } from './HiringCandidateRedirect';

const RoleRedirect = () => {
  const orgLevelGroup = useAppSelector((state) => state.orgLevelGroup);
  const userProfile = useAppSelector((state) => state.userProfile);
  const dispatch = useAppDispatch();
  const groupList = useAppSelector((state) => state.groupRoleList.data);
  const oid = useAppSelector((state) => state.orgDetails.data?.oid);
  // Team managers have admin access neither the JWT nor the profile knows about.
  const { access: adminAccess, loading: adminAccessLoading } = useAdminAccess();
  // A Hiring candidate (DA-5042) belongs to no program and holds no role, so
  // every branch below would send them to a dashboard where the backend
  // refuses every call (C-9). They go to their assessment instead.
  const candidateLanding = useHiringCandidateLanding();
  const redirect = () => {
    const { orgId, coId } = getUserDetails();
    const dispatchBreadCrumbs = (id?: string) => {
      const grpId = id ? id : 'admin';
      dispatch(
        breadCrumbsActions.removeBreadCrumbs({
          id: grpId,
          name: 'Program',
          url: `/app/programdetails/${orgId}/${coId}/${grpId}/overview`
        })
      );
    };

    let role = localStorage.getItem('role') || '';

    // Platform admins who are also members of one or more organizations get an
    // org-scoped Auth0 token after the org picker, so the `https://dosen.io/role`
    // JWT claim resolves to their in-org role (e.g. Org Admin) instead of
    // Platform Admin. The DB role (userProfile.data.role) is authoritative —
    // this mirrors the BE, which overrides the JWT role with the DB role
    // (shared/v3/handler.ts). Reconcile here so platform admins land on the
    // organizations dashboard instead of being treated as an org admin.
    if (userProfile.data?.role === 3 && role !== ROLES.platAdmin) {
      role = ROLES.platAdmin;
      localStorage.setItem('role', role);
    }

    if (role === ROLES.platAdmin) return <Navigate to="/app/organizations" />;
    // if (role === ROLES.orgAdmin) {
    //   if (orgLevelGroup.loading) return null;
    //   if (orgLevelGroup.error) return <ErrorPage message={orgLevelGroup.errorText} />;
    //   if (orgLevelGroup.data) {
    //     dispatchBreadCrumbs(orgLevelGroup.data.grpId);
    //     return <Navigate to={`/app/programdetails/${orgId}/${coId}/${orgLevelGroup.data.grpId}/overview`} />;
    //   }
    //   return <ErrorPage message="Something went wrong, please try again" />;
    // }

    if (role === ROLES.member || role === ROLES.orgAdmin || role === ROLES.grpAdmin) {
      if (userProfile.loading) return <AppLoader />;
      if (userProfile.error) return <ErrorPage message={userProfile.errorText} />;
      if (userProfile.data) {
        // Determine if this user has any admin-level access.
        // Mirrors the same logic used by ProfileOrgSettingsGuard:
        //  - JWT role is OrgAdmin (localStorage)
        //  - Profile role field is 2 (backend OrgAdmin flag)
        //  - Profile has admin groups
        //  - groupRoleList contains an admin entry
        const isOrgAdmin = role === ROLES.orgAdmin || userProfile.data?.role === 2;
        const hasAdminGroups = _.isArray(userProfile.data?.admins) && userProfile.data.admins.length > 0;
        const hasAdminInGroupList = groupList?.some((g: any) => g?.role === 'admin');
        const isAnyAdmin = isOrgAdmin || hasAdminGroups || hasAdminInGroupList;

        // Check deactivation from lastseen
        const userLastseen = localStorage.getItem('lastseen');
        const lastseen = userLastseen ? JSON.parse(userLastseen) : null;
        let lastSeenActive = true;
        if (lastseen && Object.keys(lastseen).length > 0) {
          if (lastseen.id && lastseen.role) {
            const index = groupList?.findIndex((each) => each.id === lastseen.id && each.role === lastseen.role);
            if (index !== -1) {
              lastSeenActive = _.get(lastseen, 'active', true);
            } else {
              localStorage.removeItem('lastseen');
            }
          }
        } else {
          lastSeenActive = _.get(userProfile, 'data.lastseen.active', true);
        }

        if (!lastSeenActive) {
          return <Navigate to="/app/deactivated" />;
        }

        // Admin-level users always land on the admin dashboard
        if (isAnyAdmin) {
          if (isOrgAdmin) {
            if (orgLevelGroup.loading) return <AppLoader />;
            if (orgLevelGroup.error) return <ErrorPage message={orgLevelGroup.errorText} />;
            if (orgLevelGroup.data) {
              dispatchBreadCrumbs(orgLevelGroup.data.grpId);
              dispatch(groupLoaderActions.updateState(true));
              return <Navigate to={`/app/programdetails/${orgId}/${coId}/${orgLevelGroup.data.grpId}/overview`} />;
            }
          }
          // Group-level admin or fallback
          dispatchBreadCrumbs();
          dispatch(groupLoaderActions.updateState(true));
          return <Navigate to={`/app/programdetails/${orgId}/${coId}/admin/overview`} />;
        }

        // Team managers land on the admin dashboard too — their teams' gaps.
        if (adminAccessLoading) return <AppLoader />;
        if ((adminAccess?.managedTeamIds.length ?? 0) > 0) {
          dispatchBreadCrumbs();
          dispatch(groupLoaderActions.updateState(true));
          return <Navigate to={`/app/programdetails/${orgId}/${coId}/admin/overview`} />;
        }

        // Non-admin users: use lastseen to determine landing page
        let lastSeenRole = '';
        if (lastseen && Object.keys(lastseen).length > 0 && lastseen.id && lastseen.role) {
          const index = groupList?.findIndex((each) => each.id === lastseen.id && each.role === lastseen.role);
          if (index !== -1) {
            lastSeenRole = lastseen.role;
          }
        } else {
          lastSeenRole = userProfile.data.lastseen?.role || '';
        }

        if (lastSeenRole === 'mentee' || lastSeenRole === 'mentor') {
          if (oid === AGILE_ORG && lastSeenRole === 'mentee') {
            return <Navigate to="/app/community" />;
          }
          return <Navigate to="/app/home/user" />;
        }

        // Fallback based on profile arrays
        if (_.isArray(userProfile?.data?.mentors) && userProfile.data.mentors.length > 0) {
          return <Navigate to="/app/home/user" />;
        } else if (_.isArray(userProfile?.data?.mentees) && userProfile.data.mentees.length > 0) {
          if (oid === AGILE_ORG) {
            return <Navigate to="/app/community" />;
          }
          return <Navigate to="/app/home/user" />;
        }

        // Org-level invitation roles (users.orgRoles): a learner invited with
        // no program has zero group memberships, so the match-array fallbacks
        // above find nothing — but they still belong on the learner dashboard,
        // not in /app/chat.
        const orgRoles: string[] = userProfile.data.orgRoles || [];
        if (orgRoles.includes('mentor') || orgRoles.includes('mentee')) {
          if (oid === AGILE_ORG && !orgRoles.includes('mentor')) {
            return <Navigate to="/app/community" />;
          }
          return <Navigate to="/app/home/user" />;
        }
        return <Navigate to="/app/chat" replace />;
      }
      return <ErrorPage message="Your profile could not be loaded. Refresh the page and try again." />;
    }

    return <ErrorPage message="This person does not have a role associated" />;
  };

  if (candidateLanding === 'asking') return <AppLoader />;
  if (candidateLanding === 'candidate') return <Navigate to="/hiring" replace />;
  return redirect();
};

export default RoleRedirect;
